import { useEffect, useMemo, useRef, useState } from 'react';
import { CROPS, FARM_STAGES, farmStage, type CropKey, type FarmDto } from '@bb/shared';
import { ApiError, farm as farmApi, type FarmResponse, type ProgressNews } from '../net/api';
import { drawCrop } from '../art/crops';
import { play } from '../audio/sound';
import { Thumb } from './Thumb';

type Tool = 'plant' | 'harvest' | 'dig';
const CROP_KEYS = Object.keys(CROPS) as CropKey[];

const fmtTime = (s: number) => {
  if (s >= 3600) {
    const m = Math.round((s % 3600) / 60);
    return m ? `${Math.floor(s / 3600)}h ${m}m` : `${Math.floor(s / 3600)}h`;
  }
  return s >= 60 ? `${Math.ceil(s / 60)}m` : `${Math.max(0, Math.ceil(s))}s`;
};
const perHour = (k: CropKey) => Math.round(((CROPS[k].sell - CROPS[k].seed) * 3600) / CROPS[k].growSeconds);

/** The farm behind the cottage. Drag across the field to plant, harvest or dig up many squares at once. */
export function Farm({ ownerName, onBack, onChanged }: { ownerName: string; onBack: () => void; onChanged: (news?: ProgressNews) => void }) {
  const [f, setF] = useState<FarmDto>();
  const [tool, setTool] = useState<Tool>('plant');
  const [crop, setCrop] = useState<CropKey>('carrot');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string }>();
  const [zoom, setZoom] = useState(1);
  const [, tick] = useState(0);
  const offset = useRef(0); // server clock minus ours
  const canvas = useRef<HTMLCanvasElement>(null);
  const drag = useRef<Set<number> | null>(null);
  const [hover, setHover] = useState<number>();

  const apply = (r: FarmResponse) => {
    setF(r.farm);
    offset.current = new Date(r.farm.serverTime).getTime() - Date.now();
    onChanged(r.progress);
  };
  useEffect(() => {
    farmApi.get().then(
      (x) => {
        setF(x);
        offset.current = new Date(x.serverTime).getTime() - Date.now();
      },
      (e: Error) => setMsg({ kind: 'err', text: e.message }),
    );
    const id = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const now = Date.now() + offset.current;
  const size = f?.size ?? 3;
  // Cells stay big enough to tap on small farms; big farms scroll.
  const cell = Math.max(14, Math.min(72, Math.floor(560 / size))) * zoom;
  const readyCount = f ? f.plots.filter((p) => p && new Date(p.readyAt).getTime() <= now).length : 0;
  const emptyCount = f ? f.plots.filter((p) => !p).length : 0;
  const unlocked = CROP_KEYS.filter((k) => f && CROPS[k].level <= f.level);

  useEffect(() => {
    const el = canvas.current;
    if (!el || !f) return;
    const px = size * cell;
    const dpr = window.devicePixelRatio || 1;
    el.width = px * dpr;
    el.height = px * dpr;
    el.style.width = `${px}px`;
    el.style.height = `${px}px`;
    const c = el.getContext('2d')!;
    c.setTransform(dpr, 0, 0, dpr, 0, 0);
    c.fillStyle = '#5c8f3e';
    c.fillRect(0, 0, px, px);
    f.plots.forEach((p, i) => {
      const x = (i % size) * cell;
      const y = Math.floor(i / size) * cell;
      const marked = drag.current?.has(i);
      c.fillStyle = marked ? '#a77a4f' : (i + Math.floor(i / size)) % 2 ? '#8a5f3c' : '#93673f';
      c.fillRect(x + 1, y + 1, cell - 2, cell - 2);
      if (!p) return;
      const total = new Date(p.readyAt).getTime() - new Date(p.plantedAt).getTime();
      const growth = Math.min(1, (now - new Date(p.plantedAt).getTime()) / total);
      drawCrop(c, p.crop, x + cell / 2, y + cell / 2, cell * 0.85, growth);
      if (growth >= 1 && cell >= 22) {
        c.fillStyle = 'rgba(255,255,220,.85)';
        c.beginPath();
        c.arc(x + cell - 6, y + 6, 2.5, 0, Math.PI * 2);
        c.fill();
      }
    });
    if (hover !== undefined) {
      c.strokeStyle = '#ffe9a8';
      c.lineWidth = 2;
      c.strokeRect((hover % size) * cell + 1, Math.floor(hover / size) * cell + 1, cell - 2, cell - 2);
    }
  });

  const cellAt = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const col = Math.floor(((e.clientX - r.left) / r.width) * size);
    const row = Math.floor(((e.clientY - r.top) / r.height) * size);
    return col >= 0 && row >= 0 && col < size && row < size ? row * size + col : undefined;
  };

  async function act(cells: number[]) {
    if (!f || !cells.length) return;
    try {
      if (tool === 'plant') {
        const empty = cells.filter((i) => !f.plots[i]);
        if (!empty.length) return;
        const r = await farmApi.plant(crop, empty);
        play('plant');
        apply(r);
        setMsg({ kind: 'ok', text: `Planted ${r.planted} ${CROPS[crop].name.toLowerCase()} for ${r.spent} coins.` });
      } else if (tool === 'harvest') {
        const r = await farmApi.harvest(cells);
        play('harvest');
        apply(r);
        setMsg({ kind: 'ok', text: `Harvested ${r.total} crops (+${r.xp} XP)${r.leveledUp ? ` · Level up! You're now level ${r.level}.` : ''}` });
      } else {
        apply(await farmApi.clear(cells));
      }
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof ApiError ? e.message : 'Something went wrong.' });
    }
  }
  const run = async (fn: () => Promise<FarmResponse>, ok: (r: FarmResponse & Record<string, unknown>) => string) => {
    try {
      const r = (await fn()) as FarmResponse & Record<string, unknown>;
      apply(r);
      setMsg({ kind: 'ok', text: ok(r) });
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message });
    }
  };

  const hovered = hover !== undefined ? f?.plots[hover] : undefined;
  const barn = useMemo(() => CROP_KEYS.filter((k) => (f?.barn[k] ?? 0) > 0), [f]);
  const marketLeft = f ? f.market.dailyLimit - f.market.soldToday : 0;

  return (
    <div className="farm">
      <div className="home-toolbar">
        <strong className="place">{ownerName}'s Farm</strong>
        <span className="muted">{size} × {size}</span>
        <span className="spacer" />
        <button className="btn ghost" onClick={onBack}>← Back to cottage</button>
      </div>
      {msg && <p className={msg.kind === 'ok' ? 'note-ok center' : 'form-error center'} role="status">{msg.text}</p>}
      {!f ? (
        <p className="muted center">Walking out to the field…</p>
      ) : (
        <div className="farm-body">
          <section className="farm-field">
            <div className="row-wrap farm-tools" role="toolbar" aria-label="Farm tools">
              {(['plant', 'harvest', 'dig'] as Tool[]).map((t) => (
                <button key={t} className={tool === t ? 'btn small' : 'btn small ghost'} aria-pressed={tool === t} onClick={() => setTool(t)}>
                  {t === 'plant' ? 'Plant' : t === 'harvest' ? 'Harvest' : 'Dig up'}
                </button>
              ))}
              <span className="spacer" />
              <button className="btn small" disabled={!readyCount} onClick={() => void run(() => farmApi.harvest(undefined, true), (r) => `Harvested ${r.total} crops (+${r.xp} XP).`)}>
                Harvest all ready ({readyCount})
              </button>
              <button className="btn small ghost" disabled={!emptyCount} onClick={() => void run(() => farmApi.plant(crop, undefined, true), (r) => `Planted ${r.planted} ${CROPS[crop].name.toLowerCase()} for ${r.spent} coins.`)}>
                Plant {CROPS[crop].name.toLowerCase()} everywhere ({emptyCount})
              </button>
              {size >= 15 && (
                <span className="row-wrap">
                  <button className="btn small ghost" aria-label="Zoom out" onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}>−</button>
                  <button className="btn small ghost" aria-label="Zoom in" onClick={() => setZoom((z) => Math.min(3, z + 0.25))}>+</button>
                </span>
              )}
            </div>
            {tool === 'plant' && (
              <div className="seed-row" role="radiogroup" aria-label="Seeds">
                {CROP_KEYS.map((k) => {
                  const ok = unlocked.includes(k);
                  return (
                    <button key={k} role="radio" aria-checked={crop === k} disabled={!ok} className={crop === k ? 'seed on' : 'seed'} onClick={() => setCrop(k)} title={ok ? '' : `Unlocks at ${farmStage(CROPS[k].level).size} × ${farmStage(CROPS[k].level).size}`}>
                      <Thumb item={{ type: 'crop', slot: null, render: { crop: k } }} size={34} />
                      <span className="seed-name">{CROPS[k].name}</span>
                      <span className="muted small-print">{ok ? `${CROPS[k].seed} coins · ${fmtTime(CROPS[k].growSeconds)}` : `Unlocks at ${farmStage(CROPS[k].level).size}×${farmStage(CROPS[k].level).size}`}</span>
                    </button>
                  );
                })}
              </div>
            )}
            <div className="farm-scroll">
              <canvas
                ref={canvas}
                className="farm-canvas"
                role="img"
                aria-label={`Your ${size} by ${size} farm. ${readyCount} crops ready.`}
                onPointerDown={(e) => {
                  const i = cellAt(e);
                  if (i === undefined) return;
                  e.currentTarget.setPointerCapture(e.pointerId);
                  drag.current = new Set([i]);
                  tick((n) => n + 1);
                }}
                onPointerMove={(e) => {
                  const i = cellAt(e);
                  setHover(i);
                  if (drag.current && i !== undefined) drag.current.add(i);
                }}
                onPointerUp={() => {
                  const cells = drag.current ? [...drag.current] : [];
                  drag.current = null;
                  void act(cells);
                }}
                onPointerLeave={() => setHover(undefined)}
              />
            </div>
            <p className="muted small-print">
              {hovered
                ? new Date(hovered.readyAt).getTime() <= now
                  ? `${CROPS[hovered.crop].name}: ready to harvest!`
                  : `${CROPS[hovered.crop].name}: ready in ${fmtTime((new Date(hovered.readyAt).getTime() - now) / 1000)}`
                : 'Tip: click and drag across the field to work on lots of squares at once.'}
            </p>
          </section>

          <aside className="farm-side">
            <section className="card-ish">
              <h3>Market stand</h3>
              <p className="muted small-print">
                Today: {f.market.soldToday.toLocaleString()} / {f.market.dailyLimit.toLocaleString()} coins. Crops over the limit wait in your backpack for tomorrow.
              </p>
              {barn.length === 0 ? (
                <p className="muted small-print">Nothing harvested yet.</p>
              ) : (
                <>
                  <ul className="barn">
                    {barn.map((k) => (
                      <li key={k}>
                        <Thumb item={{ type: 'crop', slot: null, render: { crop: k } }} size={28} />
                        <span>{CROPS[k].name} × {f.barn[k]}</span>
                        <span className="muted small-print">{CROPS[k].sell} each</span>
                        <button className="btn small ghost" disabled={marketLeft < CROPS[k].sell} onClick={() => void run(() => farmApi.sell(k), (r) => `Sold for ${r.coins} coins.`)}>Sell</button>
                      </li>
                    ))}
                  </ul>
                  <button className="btn wide" disabled={marketLeft <= 0} onClick={() => void run(() => farmApi.sell(), (r) => `Sold for ${r.coins} coins.`)}>Sell everything</button>
                </>
              )}
            </section>

            <section className="card-ish">
              <h3>Grow your farm</h3>
              {f.next ? (
                <>
                  <p>
                    Next: <strong>{f.next.size} × {f.next.size}</strong> ({(f.next.size * f.next.size).toLocaleString()} squares, now {(size * size).toLocaleString()})
                  </p>
                  <ul className="small-print">
                    <li>Unlocks {f.next.unlocks.map((k) => CROPS[k as CropKey].name).join(', ')}</li>
                    <li>Market pays up to {farmStage(f.next.level).dailyMarket.toLocaleString()} coins a day (now {f.market.dailyLimit.toLocaleString()})</li>
                    {FARM_STAGES[f.next.level - 1]?.gift && <li>Comes with a free cottage decoration</li>}
                  </ul>
                  <button className="btn wide" onClick={() => void run(() => farmApi.upgrade(), (r) => `Your farm is now ${r.farm.size} × ${r.farm.size}!${r.gift ? ` You got a ${r.gift} for your cottage.` : ''}`)}>
                    Upgrade for {f.next.cost.toLocaleString()} coins
                  </button>
                </>
              ) : (
                <p>Your farm is as big as it gets. Master farmer!</p>
              )}
            </section>

            <section className="card-ish">
              <h3>Crop guide</h3>
              <table className="crop-guide">
                <thead><tr><th>Crop</th><th>Grows</th><th>Seed</th><th>Sells</th><th title="Profit per square per hour if you replant right away">Per hour</th></tr></thead>
                <tbody>
                  {CROP_KEYS.map((k) => (
                    <tr key={k} className={unlocked.includes(k) ? '' : 'locked'}>
                      <td>{CROPS[k].name}</td><td>{fmtTime(CROPS[k].growSeconds)}</td><td>{CROPS[k].seed}</td><td>{CROPS[k].sell}</td><td>{perHour(k)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="muted small-print">Quick crops earn the most if you keep replanting; slow crops earn more each time you check in.</p>
            </section>
          </aside>
        </div>
      )}
    </div>
  );
}
